import { useCallback } from 'react';
import { useSetAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';

import { powertrainAtom } from 'atoms/powertrainAtom';
import { stageConfigAtom as rStageConfigAtom } from 'atoms/Rtool/stageConfigAtoms';
import { stageConfigAtom as lStageConfigAtom } from 'atoms/Ltool/stageConfigAtoms';
import type { Config as RConfig } from 'atoms/Rtool/newConfigAtoms';
import type { Config as LConfig } from 'atoms/Ltool/newConfigAtoms';
import type { RecentCalc, RecentCalcConfig, RecentCalcTool } from 'atoms/recentCalcsAtom';

// 直近計算セットの再選択ロジック（CalcSetIndicator とコマンドパレットで共有する単一実装）。
// 純粋な変換部（config → 各 stageConfig、tool → 遷移先 path）を分離して単体検証可能にする。

// R は unit_id を持つ。RecentCalcConfig の任意フィールドは既定 '' で埋める（従来の再選択と同一）。
export const toRStageConfig = (c: RecentCalcConfig): RConfig => ({
    project_id: c.project_id,
    destination_id: c.destination_id,
    lot_id: c.lot_id,
    phase_id: c.phase_id,
    unit_id: c.unit_id ?? '',
    calculation_id: c.calculation_id,
    condition_id: c.condition_id ?? '',
    graph_id: c.graph_id ?? '',
});

// L は unit_id を持たない。
export const toLStageConfig = (c: RecentCalcConfig): LConfig => ({
    project_id: c.project_id,
    destination_id: c.destination_id,
    lot_id: c.lot_id,
    phase_id: c.phase_id,
    calculation_id: c.calculation_id,
    condition_id: c.condition_id ?? '',
    graph_id: c.graph_id ?? '',
});

// 再選択後の遷移先（計算セット選択画面）。
export const reselectPath = (tool: RecentCalcTool): string => `/${tool}-tools/existing-project`;

// powertrain と該当 stageConfig を復元し、計算セット選択画面へ遷移する。
// 既存アクション（atom 書き込み + useNavigate）の呼び出しのみで新規副作用は作らない。
export const useReselectCalc = (): ((entry: RecentCalc) => void) => {
    const setPowertrain = useSetAtom(powertrainAtom);
    const setRStage = useSetAtom(rStageConfigAtom);
    const setLStage = useSetAtom(lStageConfigAtom);
    const navigate = useNavigate();

    return useCallback(
        (entry: RecentCalc) => {
            setPowertrain(entry.powertrain);
            if (entry.tool === 'r') {
                setRStage(toRStageConfig(entry.config));
            } else {
                setLStage(toLStageConfig(entry.config));
            }
            navigate(reselectPath(entry.tool));
        },
        [setPowertrain, setRStage, setLStage, navigate],
    );
};
